/**
 * Vigia de esgotados — lista editável de SKUs que acabaram de vez; o serviço (services/vigia-esgotados, porta 8111)
 * lê de hora em hora todos os anúncios, variações e kits desses SKUs no ML (Matriz/Extrema), na Shopee (Festa/Decora) e,
 * desde a fase 2 (29/10/2026), na Shein, no TikTok e na Amazon
 * e abre alerta quando algum está VENDÁVEL — inclusive com estoque reservado em oferta relâmpago da Shopee.
 * O popup global (components/VigiaEsgotadosPopup) mostra os alertas em qualquer página.
 * Pedido do Felipe, 28/10/2026. Fora do SourceScopeGuard: só fala com o serviço 8111.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, FormControl, FormControlLabel, InputLabel, Link, MenuItem, Select, Stack,
  Switch, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, TextField, Typography,
} from '@mui/material'
import { DomainPageShell } from '../components/DomainPageShell'
import { BotoesAlerta, LinhaAlerta } from '../components/VigiaEsgotadosPopup'
import { PropostaIaCard, type PropostaIaState } from '../components/PropostaIaCard'
import { usePanelInsightsFoco, useRegisterPanelInsights } from '../state/PanelInsightsContext'
import { pedirAnaliseProposta } from '../api/bridge'
import {
  fetchAlertas, fetchAnuncios, fetchSkus, fetchStatus, incluirSkus, reler, removerSku,
  type AlertaVigia, type AnuncioVigiado, type SkuVigiado, type StatusVigia,
} from './vigiaEsgotados/vigiaService'
import { LOJAS, canaisComFalha, descricaoSku, filtrarAnuncios, logistica, loja, quando, separarSkus, ultimaAcao, type FiltroAnuncios } from './vigiaEsgotados/logic'

const TITULO = 'Vigia de esgotados'
const msg = (e: unknown) => (e instanceof Error ? e.message : String(e))
const n = (v: number | null | undefined) => (v == null ? '—' : v.toLocaleString('pt-BR'))

function AbaSkus({ skus, escreve, onMudou }: { skus: SkuVigiado[]; escreve: boolean; onMudou: () => void }) {
  const [texto, setTexto] = useState('')
  const [motivo, setMotivo] = useState('')
  const [busy, setBusy] = useState(false)
  const [aviso, setAviso] = useState<{ msg: string; ok: boolean } | null>(null)
  const novos = separarSkus(texto)

  const incluir = async () => {
    setBusy(true)
    setAviso(null)
    try {
      const r = await incluirSkus(novos.join('\n'), motivo)
      const fora = r.fora_do_tiny.length ? ` Atenção: ${r.fora_do_tiny.join(', ')} não existe(m) no Tiny — confira a grafia.` : ''
      setAviso({ msg: `${r.incluidos.length} SKU(s) na lista; ${r.mensagem}.${fora}`, ok: !r.fora_do_tiny.length })
      setTexto('')
      setMotivo('')
      onMudou()
    } catch (e) {
      setAviso({ msg: msg(e), ok: false })
    } finally {
      setBusy(false)
    }
  }
  const remover = async (s: SkuVigiado) => {
    if (!window.confirm(`Tirar ${s.sku} da lista? Os alertas dele fecham na próxima leitura.`)) return
    try {
      await removerSku(s.sku)
      onMudou()
    } catch (e) {
      setAviso({ msg: msg(e), ok: false })
    }
  }

  return (
    <Stack spacing={2}>
      {escreve ? (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'flex-start' }}>
          <TextField label="SKUs (cole vários: um por linha ou separados por vírgula)" multiline minRows={2} maxRows={8} value={texto}
            onChange={(e) => setTexto(e.target.value)} sx={{ flex: 2 }} size="small" />
          <TextField label="Motivo (opcional)" value={motivo} onChange={(e) => setMotivo(e.target.value)} sx={{ flex: 1 }} size="small" />
          <Button variant="contained" disabled={busy || !novos.length} onClick={() => void incluir()}>
            {busy ? <CircularProgress size={16} /> : `Incluir ${novos.length || ''}`}
          </Button>
        </Stack>
      ) : (
        <Alert severity="info">Serviço em modo leitura (sem token no servidor): não dá para editar a lista.</Alert>
      )}
      {aviso ? <Alert severity={aviso.ok ? 'success' : 'warning'} onClose={() => setAviso(null)}>{aviso.msg}</Alert> : null}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>SKU</TableCell>
            <TableCell>Motivo</TableCell>
            <TableCell>Kits que usam</TableCell>
            <TableCell align="right">Anúncios achados</TableCell>
            <TableCell align="right">Vendáveis agora</TableCell>
            <TableCell>Incluído</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {skus.map((s) => (
            <TableRow key={s.id}>
              <TableCell sx={{ fontWeight: 600 }}>{s.sku}</TableCell>
              <TableCell>{s.motivo || '—'}</TableCell>
              <TableCell sx={{ maxWidth: 280 }}>{s.kits == null ? 'aguardando leitura' : s.kits.length ? s.kits.join(', ') : 'nenhum'}</TableCell>
              <TableCell align="right">{n(s.anuncios)}</TableCell>
              <TableCell align="right" sx={{ color: s.vendaveis ? 'error.main' : undefined, fontWeight: s.vendaveis ? 700 : undefined }}>{n(s.vendaveis)}</TableCell>
              <TableCell>{quando(s.criado_em)} · {s.criado_por}</TableCell>
              <TableCell align="right">{escreve ? <Button size="small" color="inherit" onClick={() => void remover(s)}>Remover</Button> : null}</TableCell>
            </TableRow>
          ))}
          {!skus.length ? <TableRow><TableCell colSpan={7}><Typography color="text.secondary">A lista está vazia.</Typography></TableCell></TableRow> : null}
        </TableBody>
      </Table>
    </Stack>
  )
}

function AbaAnuncios({ itens }: { itens: AnuncioVigiado[] }) {
  const [f, setF] = useState<FiltroAnuncios>({ texto: '', loja: '', soVendaveis: false })
  const lista = useMemo(() => filtrarAnuncios(itens, f), [itens, f])
  return (
    <Stack spacing={1.5}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
        <TextField size="small" label="SKU, título ou ID" value={f.texto} onChange={(e) => setF({ ...f, texto: e.target.value })} sx={{ minWidth: 260 }} />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Loja</InputLabel>
          <Select label="Loja" value={f.loja} onChange={(e) => setF({ ...f, loja: e.target.value })}>
            <MenuItem value="">Todas</MenuItem>
            {Object.entries(LOJAS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControlLabel control={<Switch checked={f.soVendaveis} onChange={(e) => setF({ ...f, soVendaveis: e.target.checked })} />} label="Só vendáveis" />
        <Typography variant="caption" color="text.secondary">{n(lista.length)} de {n(itens.length)}</Typography>
      </Stack>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Loja</TableCell>
            <TableCell>Anúncio</TableCell>
            <TableCell>SKU</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Estoque</TableCell>
            <TableCell align="right">Reservado em promoção</TableCell>
            <TableCell>Vendável?</TableCell>
            <TableCell>Lido em</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {lista.map((a) => (
            <TableRow key={a.id} sx={a.vendavel ? { '& td': { color: 'error.main' } } : undefined}>
              <TableCell>{loja(a.loja)}{a.full ? ` · ${logistica(a)}` : ''}</TableCell>
              <TableCell sx={{ maxWidth: 320 }}>
                <Link href={a.link || undefined} target="_blank" rel="noopener">{a.item_id}</Link>
                <Typography variant="caption" sx={{ display: 'block' }} noWrap>{a.titulo}</Typography>
              </TableCell>
              <TableCell>{descricaoSku(a)}</TableCell>
              <TableCell>{a.status}</TableCell>
              <TableCell align="right">{n(a.estoque)}</TableCell>
              <TableCell align="right">{n(a.reservado_promo)}</TableCell>
              <TableCell>{a.vendavel ? <Chip size="small" color="error" label={a.motivo || 'sim'} /> : 'não'}</TableCell>
              <TableCell>{quando(a.lido_em)}</TableCell>
            </TableRow>
          ))}
          {!lista.length ? <TableRow><TableCell colSpan={8}><Typography color="text.secondary">Nenhum anúncio.</Typography></TableCell></TableRow> : null}
        </TableBody>
      </Table>
    </Stack>
  )
}

function AbaAlertas({ itens, escreve, onMudou }: { itens: AlertaVigia[]; escreve: boolean; onMudou: () => void }) {
  const [aviso, setAviso] = useState<{ msg: string; ok: boolean } | null>(null)
  const abertos = itens.filter((a) => !a.fechado_em)
  const fechados = itens.filter((a) => a.fechado_em)
  return (
    <Stack spacing={2}>
      {aviso ? <Alert severity={aviso.ok ? 'success' : 'error'} onClose={() => setAviso(null)}>{aviso.msg}</Alert> : null}
      <Typography variant="subtitle2">Abertos ({abertos.length})</Typography>
      {abertos.map((a) => (
        <Card key={a.id} variant="outlined">
          <CardContent>
            <Stack spacing={1}>
              <LinhaAlerta a={a} />
              <BotoesAlerta a={a} escreve={escreve} onFeito={(m, ok) => { setAviso({ msg: m, ok }); onMudou() }} />
              {a.acoes.length ? <Typography variant="caption" color="text.secondary">Última ação: {ultimaAcao(a)}</Typography> : null}
            </Stack>
          </CardContent>
        </Card>
      ))}
      {!abertos.length ? <Typography color="text.secondary">Nenhum alerta aberto.</Typography> : null}
      <Typography variant="subtitle2">Histórico</Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Loja</TableCell>
            <TableCell>Anúncio</TableCell>
            <TableCell>SKU</TableCell>
            <TableCell>Motivo</TableCell>
            <TableCell>Aberto</TableCell>
            <TableCell>Fechado</TableCell>
            <TableCell>Ação</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {fechados.map((a) => (
            <TableRow key={a.id}>
              <TableCell>{loja(a.loja)}</TableCell>
              <TableCell><Link href={a.link || undefined} target="_blank" rel="noopener">{a.item_id}</Link></TableCell>
              <TableCell>{descricaoSku(a)}</TableCell>
              <TableCell>{a.motivo}</TableCell>
              <TableCell>{quando(a.aberto_em)}</TableCell>
              <TableCell>{quando(a.fechado_em)} · {a.fechado_por === 'acao' ? 'pausado pela equipe' : 'deixou de ser vendável'}</TableCell>
              <TableCell>{ultimaAcao(a)}</TableCell>
            </TableRow>
          ))}
          {!fechados.length ? <TableRow><TableCell colSpan={7}><Typography color="text.secondary">Sem histórico ainda.</Typography></TableCell></TableRow> : null}
        </TableBody>
      </Table>
    </Stack>
  )
}

export function VigiaEsgotadosPage() {
  const [aba, setAba] = useState(0)
  const [st, setSt] = useState<StatusVigia | null>(null)
  const [skus, setSkus] = useState<SkuVigiado[]>([])
  const [anuncios, setAnuncios] = useState<AnuncioVigiado[]>([])
  const [alertas, setAlertas] = useState<AlertaVigia[]>([])
  const [erro, setErro] = useState<string | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [aviso, setAviso] = useState<string | null>(null)
  const [leituraIa, setLeituraIa] = useState<PropostaIaState>({})

  const carregar = useCallback(async () => {
    try {
      const [s, k, an, al] = await Promise.all([fetchStatus(), fetchSkus(), fetchAnuncios(), fetchAlertas('todos')])
      setSt(s)
      setSkus(k)
      setAnuncios(an)
      setAlertas(al)
      setErro(null)
    } catch (e) {
      setErro(msg(e))
    } finally {
      setCarregando(false)
    }
  }, [])

  useEffect(() => {
    void carregar()
    const t = window.setInterval(() => void carregar(), 60_000)
    return () => window.clearInterval(t)
  }, [carregar])

  const abertos = alertas.filter((a) => !a.fechado_em)
  const vendaveis = anuncios.filter((a) => a.vendavel)
  const falhas = canaisComFalha(st?.canais)

  const { focoRef } = usePanelInsightsFoco()
  const carregarLeitura = useCallback(async () => {
    if (!st) return
    setLeituraIa((p) => ({ ...p, loading: true, erro: null }))
    const fallback = [
      `${skus.length} SKUs na lista de esgotados · ${anuncios.length} anúncios/variações vigiados · ${vendaveis.length} vendáveis agora.`,
      `${abertos.length} alertas abertos. Última leitura ${quando(st.ultimo_ciclo)}.`,
      falhas.length ? `Leitura com falha: ${falhas.join(' | ')} — os alertas desses canais não fecham até a leitura voltar.` : 'Todos os canais lidos.',
    ].join('\n')
    try {
      const data = await pedirAnaliseProposta({
        tipo: 'insight_reflexao',
        insight: {
          dominio: 'vigia_esgotados',
          dominio_label: TITULO,
          titulo: 'Leitura do vigia de esgotados',
          leitura: fallback,
          ...(focoRef.current ? { foco_usuario: focoRef.current } : {}),
          metricas: { skus: skus.length, anuncios: anuncios.length, vendaveis: vendaveis.length, alertas_abertos: abertos.length, canais_com_falha: falhas.length },
        },
      })
      setLeituraIa(data.ok
        ? { loading: false, texto: data.leitura || data.texto || fallback, modelo: data.modelo, fonte: data.fonte, cached: data.cached, gerado_em: data.gerado_em }
        : { loading: false, texto: fallback, fonte: 'regras', modelo: null, erro: null })
    } catch {
      setLeituraIa({ loading: false, texto: fallback, fonte: 'regras', modelo: null, erro: null })
    }
  }, [st, skus.length, anuncios.length, vendaveis.length, abertos.length, falhas, focoRef])

  useEffect(() => {
    if (st) void carregarLeitura()
    // só na primeira carga e quando muda a última leitura do serviço
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [st?.ultimo_ciclo])

  useRegisterPanelInsights({
    titulo: TITULO,
    verdade: 'Verdade = lista, anúncios e alertas desta tela (serviço /api/vigia-esgotados). Proposta = leitura da IA — revise antes de agir.',
    estado: leituraIa,
    recarregar: carregarLeitura,
  })

  const pedirReler = async () => {
    try {
      setAviso((await reler()).mensagem)
      void carregar()
    } catch (e) {
      setAviso(msg(e))
    }
  }

  return (
    <DomainPageShell
      title={TITULO}
      subtitle={st ? `Última leitura ${quando(st.ultimo_ciclo)} · relê a cada ${st.ciclo_min} min · ML Matriz e Extrema, Shopee Festa e Decora, Shein, TikTok e Amazon` : 'carregando…'}
      loading={carregando}
      error={erro}
      onRetry={() => void carregar()}
      stats={st ? [
        { label: 'SKUs na lista', value: n(skus.length) },
        { label: 'Anúncios vigiados', value: n(anuncios.length), hint: 'anúncios, variações e kits' },
        { label: 'Vendáveis agora', value: n(vendaveis.length), hint: 'ativos com estoque ou reserva' },
        { label: 'Alertas abertos', value: n(abertos.length) },
      ] : undefined}
    >
      <Stack spacing={2}>
        {st?.rodando_desde ? <Alert severity="info">Lendo os anúncios agora (desde {quando(st.rodando_desde)})…</Alert> : null}
        {st?.erro ? <Alert severity="error">A última leitura falhou: {st.erro}</Alert> : null}
        {falhas.length ? (
          <Alert severity="warning">
            Leitura com falha em: {falhas.join(' · ')}. Os alertas desses canais ficam abertos até a leitura voltar — falha não é "tudo ok".
          </Alert>
        ) : null}
        {aviso ? <Alert severity="info" onClose={() => setAviso(null)}>{aviso}</Alert> : null}
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Tabs value={aba} onChange={(_, v: number) => setAba(v)} sx={{ flex: 1 }} variant="scrollable">
                <Tab label={`SKUs vigiados (${skus.length})`} />
                <Tab label={`Anúncios vigiados (${anuncios.length})`} />
                <Tab label={`Alertas (${abertos.length} abertos)`} />
              </Tabs>
              {st?.escrita ? <Button size="small" variant="outlined" disabled={!!st.rodando_desde} onClick={() => void pedirReler()}>Reler agora</Button> : null}
            </Stack>
            <Box>
              {aba === 0 && <AbaSkus skus={skus} escreve={!!st?.escrita} onMudou={() => void carregar()} />}
              {aba === 1 && <AbaAnuncios itens={anuncios} />}
              {aba === 2 && <AbaAlertas itens={alertas} escreve={!!st?.escrita} onMudou={() => void carregar()} />}
            </Box>
          </CardContent>
        </Card>
        <PropostaIaCard
          title="Leitura do vigia"
          caption="Verdade = lista, anúncios e alertas. Proposta = leitura da IA — revise antes de pausar."
          state={leituraIa}
          onRetry={() => void carregarLeitura()}
        />
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.7 }}>
          Vendável = ML ativo com estoque disponível (inclusive Full) · Shopee ativo com estoque, ou com estoque reservado em promoção/oferta
          relâmpago (foi o caso que já gerou venda sem estoque) · Shein no ar com estoque · TikTok ativo com estoque · Amazon comprável
          (no FBA o alerta vem sem botão: o estoque está na Amazon). Kit entra se usar um SKU da lista (composição do Tiny). Nada é pausado
          sozinho: só pelo botão. Tray fora (nenhum serviço lê anúncios da Tray).
        </Typography>
      </Stack>
    </DomainPageShell>
  )
}
